import React from "react";
import { Play, Square, Loader2 } from "lucide-react";
import { COLORS } from "../constants";
import { pill, fontSerif } from "./styles";

/**
 * TourButton — start/stop the guided camera tour through every marker.
 *
 * Now positioned as a primary action at the bottom-right of the viewport,
 * just above the Landmarks legend. Idle state uses a subtle gold tint so it
 * reads as a featured CTA; active state goes full gold + spinner.
 */
export default function TourButton({
  active,
  count,
  currentIndex,
  onStart,
  onStop,
  compact = false,
  bottomOffset = 158,   // sits above LandmarksLegend (bottom: 22 + ~ legend height)
}) {
  const label = active
    ? `Tour · ${Math.min(currentIndex + 1, count)} / ${count}`
    : "Start Tour";
  const Icon = active ? Square : Play;

  const buttonStyle = {
    ...pill,
    padding: compact ? "7px 12px" : "10px 16px",
    color: active ? COLORS.navyDeep : COLORS.goldBright,
    background: active
      ? COLORS.gold
      : "linear-gradient(180deg, rgba(230,180,80,0.22) 0%, rgba(230,180,80,0.10) 100%)",
    fontFamily: fontSerif,
    fontSize: compact ? 11 : 12,
    letterSpacing: 2,
    textTransform: "uppercase",
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: `1px solid ${COLORS.gold}`,
    boxShadow: active
      ? "0 10px 24px rgba(230,180,80,0.30)"
      : "0 8px 22px rgba(0,0,0,0.35), 0 0 22px rgba(230,180,80,0.10)",
    transition: "background-color .2s, color .2s, transform .12s",
  };

  return (
    <div
      data-testid="zone-tour-wrapper"
      style={{
        position: "absolute",
        right: compact ? 14 : 24,
        bottom: bottomOffset,
        pointerEvents: "auto",
        zIndex: 6,
      }}
    >
      <button
        data-testid={active ? "zone-tour-stop" : "zone-tour-start"}
        onClick={active ? onStop : onStart}
        title={
          active
            ? "Stop the guided tour"
            : `Auto-fly the camera through all ${count} markers`
        }
        style={buttonStyle}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = "translateY(1px)";
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        {active && <Loader2 size={12} className="spin" />}
        <Icon size={13} color={active ? COLORS.navyDeep : COLORS.gold} />
        {label}
      </button>
    </div>
  );
}
